import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { courses } from "@/data/courses";
import { CourseCard } from "@/components/CourseCard";
import { Marquee } from "@/components/Marquee";
import { Testimonials } from "@/components/Testimonials";
import { Seo } from "@/components/Seo";

const fade = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6 },
};

const pillars = [
  { t: "Praktičnost", d: "Soustředíme se výhradně na dovednosti, které mají reálné využití v každodenním životě." },
  { t: "Originalita", d: "Naše unikátní vzdělávací metody se vymykají standardním kurzům dostupným na trhu." },
  { t: "Profesionalita", d: "Naši lektoři jsou experti, kteří své obory znají skrz naskrz a dokáží znalosti efektivně předat." },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-gavito-light">
      <Seo title="Vzdělávací agentura – kurzy AI a digitálních dovedností" description="GAVITO technic s.r.o. z Brna – praktické kurzy umělé inteligence, digitálních dovedností a kybernetické bezpečnosti. Vzděláváme s láskou." path="/" />
      {/* HERO */}
      <section className="relative pt-36 md:pt-48 pb-24 overflow-hidden">
        <div className="absolute top-24 right-[-6rem] w-[26rem] h-[26rem] bg-gavito-green/15 rounded-full blur-3xl hidden md:block pointer-events-none" />
        <div className="absolute top-40 right-10 w-52 h-52 bg-gavito-green/25 rounded-[3rem] rotate-12 hidden lg:block pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-gavito-green mb-6">
              Vzdělávací agentura · Brno
            </p>
            <h1 className="font-heading font-black text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.95] text-gavito-navy max-w-4xl">
              Digitální <span className="text-gavito-green">dovednosti</span> pro všechny.
            </h1>
            <p className="mt-8 text-lg md:text-xl font-medium text-gavito-navy/70 max-w-xl leading-relaxed">
              Přinášíme praktické vzdělávání v oblasti digitálních kompetencí a umělé inteligence.
              Neplýtváme vaším časem na obecné teorie — učíme konkrétní dovednosti do praxe.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/kurzy" data-testid="hero-cta-courses" className="inline-flex items-center gap-2 bg-gavito-navy text-white font-semibold px-8 py-4 rounded-full hover:bg-gavito-green transition-colors">
                Prohlédnout kurzy <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/o-nas" data-testid="hero-cta-about" className="inline-flex items-center gap-2 border border-gavito-navy/20 text-gavito-navy font-semibold px-8 py-4 rounded-full hover:bg-gavito-navy hover:text-white transition-colors">
                Proč my
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE STAT */}
      <section className="bg-gavito-navy py-7 md:py-9">
        <Marquee
          text="ZA POSLEDNÍCH 12 MĚSÍCŮ U NÁS ZÍSKALO NOVÉ DOVEDNOSTI PŘES 2 000 LIDÍ"
          className="text-4xl md:text-6xl text-gavito-green"
        />
      </section>

      {/* WHY US */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <motion.div {...fade} className="lg:col-span-4">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-gavito-green mb-4">Proč my</p>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-gavito-navy leading-[1.05]">
                Vzděláváme úplně jinak, než jste zvyklí.
              </h2>
              <Link to="/o-nas" className="inline-flex items-center gap-2 mt-8 text-gavito-navy font-semibold hover:text-gavito-green transition-colors">
                Více o nás <ArrowUpRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
              {pillars.map((p, i) => (
                <motion.div key={p.t} {...fade} transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white border border-gavito-navy/10 rounded-3xl p-8">
                  <span className="font-heading font-black text-4xl text-gavito-green/30">0{i + 1}</span>
                  <h3 className="font-heading font-bold text-2xl text-gavito-navy mt-4">{p.t}</h3>
                  <p className="mt-3 text-gavito-navy/65 leading-relaxed">{p.d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-gavito-navy text-white rounded-[2.5rem] px-8 md:px-16 py-16 md:py-20 grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fade}>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-gavito-green mb-4">Výhody vzdělávání s GAVITO</p>
              <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter leading-[1.02]">
                Co vás <span className="text-gavito-green">čeká</span>
              </h2>
            </motion.div>
            <motion.div {...fade} transition={{ duration: 0.6, delay: 0.1 }}>
              <p className="text-lg text-white/70 leading-relaxed">
                Nejsme klasická vzdělávací agentura. Učíme jinak, prakticky a s nadšením – tak, abyste si
                vše ihned vyzkoušeli na svých vlastních úlohách.
              </p>
              <ul className="mt-8 space-y-4">
                {[
                  "Praktické dovednosti použitelné od prvního dne",
                  "Zkušení lektoři z reálné praxe",
                  "Malé skupiny a individuální přístup",
                  "Online, kombinovaně i prezenčně – podle toho, co vám vyhovuje",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-lg">
                    <span className="w-6 h-6 rounded-full bg-gavito-green/20 flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-gavito-green" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
              <Link to="/kurzy" data-testid="advantages-cta" className="inline-flex items-center gap-2 mt-8 bg-gavito-green text-white font-semibold px-8 py-4 rounded-full hover:bg-white hover:text-gavito-navy transition-colors">
                Zobrazit kurzy <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COURSES TEASER */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-gavito-green mb-4">Nabídka</p>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight text-gavito-navy">Naše kurzy</h2>
            </div>
            <Link to="/kurzy" data-testid="teaser-all-courses" className="inline-flex items-center gap-2 text-gavito-navy font-semibold hover:text-gavito-green transition-colors">
              Všechny kurzy <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.slice(0, 3).map((c, i) => <CourseCard key={c.slug} course={c} index={i} />)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />
    </div>
  );
}
